import { IContentPackItem, ITEM_TYPE } from '@sitegen/common/types';
import { IRedeemContent } from '../redemption.reducer';
import { IContentToDisplay, IRedemptionSuccessProps } from './redemption-success.types';

import { REDEMPTION_CONTENT_STATUSES, TEXT_KEYS } from '../redemption.constants';
import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import { WIDGET_NAME } from '@sitegen/common/constants';

/** Content pack ID is based on item name, like "The Crew 2 - AMG C63" => "the_crew_2_-_amg_c63" and is stored in database */
export const getContentPackId = (name: string): string => {
    return name ? name.replace(/\ /g, '_').toLowerCase() : '';
};

/** Server response is filtered by list of configured in SiteGen content packs and item types */
export const getContentToDisplay = (options: IRedemptionSuccessProps): IContentToDisplay => {
    const contentPackItems: IContentPackItem[] = options.contentPackItems || [];
    const unlockedContent: IRedeemContent[] = options.unlockedContent || [];

    const result: {} = {};
    for (const contentPackItem of contentPackItems) {
        const packId: string = String(contentPackItem.packId);

        /* Match content pack items listed in config to response result */
        const responseItem: IRedeemContent | undefined = unlockedContent.find((item: IRedeemContent): boolean => {
            return getContentPackId(item.itemName) === packId;
        });

        if (!responseItem) {
            continue;
        }

        /* Some types should not be visible */
        const isValidType: boolean = ITEM_TYPE.POPULATION !== responseItem.redeemActionType;
        if (isValidType) {
            result[packId] = { contentPackItem: contentPackItem, responseItem };
        }
    }

    return result;
};

export const buildContentItemTranslationKey = (
    contentItem: IRedeemContent,
    platformId: PLATFORM_TYPES | null,
    widgetName: WIDGET_NAME
): string => {
    const { status, redeemActionType, redeemActionDetails } = contentItem;
    const { key: keyToRedeemLater } = redeemActionDetails;
    const { PENDING, REDEEMED_AND_DELIVERED, REDEEMED_NOT_ACTIVATED } = REDEMPTION_CONTENT_STATUSES;
    const hasCFRedeemNoPlatform = widgetName === WIDGET_NAME.CF_REDEEM && !platformId;

    const keyDistributionPcKeys = {
        [REDEEMED_AND_DELIVERED]: TEXT_KEYS.PC_KEY_DISTRIBUTION_DELIVERED,
        [REDEEMED_NOT_ACTIVATED]: keyToRedeemLater
            ? TEXT_KEYS.PC_KEY_DISTRIBUTION_NOT_ACTIVATED_KEY
            : TEXT_KEYS.GENERAL_KEY_DISTRIBUTION_NO_KEY,
        [PENDING]: keyToRedeemLater
            ? TEXT_KEYS.PC_KEY_DISTRIBUTION_NOT_ACTIVATED_KEY
            : TEXT_KEYS.GENERAL_KEY_DISTRIBUTION_NO_KEY
    };

    const keyDistributionConsoleKeys = {
        [REDEEMED_AND_DELIVERED]: TEXT_KEYS.CONSOLE_KEY_DISTRIBUTION_DELIVERED_OR_NOT_ACTIVATED,
        [REDEEMED_NOT_ACTIVATED]: keyToRedeemLater
            ? TEXT_KEYS.CONSOLE_KEY_DISTRIBUTION_DELIVERED_OR_NOT_ACTIVATED
            : TEXT_KEYS.GENERAL_KEY_DISTRIBUTION_NO_KEY,
        [PENDING]: keyToRedeemLater
            ? TEXT_KEYS.CONSOLE_KEY_DISTRIBUTION_DELIVERED_OR_NOT_ACTIVATED
            : TEXT_KEYS.GENERAL_KEY_DISTRIBUTION_NO_KEY
    };

    /* Map which contains TEXT_KEYS in format:
      [REDEMPTION_ACTION_TYPE][REDEMPTION_CONTENT_STATUS]: TEXT_KEY */
    const KEYS_PER_STATUS_AND_ACTION_TYPE = {
        [ITEM_TYPE.BETA]: TEXT_KEYS.BETA_ACCESS,
        [ITEM_TYPE.SECONDARY_STORE_ITEM]: {
            [REDEEMED_AND_DELIVERED]: TEXT_KEYS.SECONDARY_STORE_DELIVERED,
            [REDEEMED_NOT_ACTIVATED]: TEXT_KEYS.SECONDARY_STORE_NOT_ACTIVATED,
            [PENDING]: TEXT_KEYS.SECONDARY_STORE_NOT_ACTIVATED
        },
        [ITEM_TYPE.SECONDARY_STORE_OFFER]: {
            [REDEEMED_AND_DELIVERED]: TEXT_KEYS.SECONDARY_STORE_DELIVERED,
            [REDEEMED_NOT_ACTIVATED]: TEXT_KEYS.SECONDARY_STORE_NOT_ACTIVATED,
            [PENDING]: TEXT_KEYS.SECONDARY_STORE_NOT_ACTIVATED
        },
        [ITEM_TYPE.KEY_DISTRIBUTION]: platformId === PLATFORM_TYPES.PC || hasCFRedeemNoPlatform
            ? keyDistributionPcKeys
            : keyDistributionConsoleKeys,
        [ITEM_TYPE.CLUB_REWARD]: {
            [REDEEMED_AND_DELIVERED]: TEXT_KEYS.CLUB_REWARD_DELIVERED,
            [REDEEMED_NOT_ACTIVATED]: TEXT_KEYS.CLUB_REWARD_NOT_ACTIVATED,
            [PENDING]: TEXT_KEYS.CLUB_REWARD_NOT_ACTIVATED
        },
        [ITEM_TYPE.CLUB_ACTION]: {
            [REDEEMED_AND_DELIVERED]: TEXT_KEYS.CLUB_REWARD_DELIVERED,
            [REDEEMED_NOT_ACTIVATED]: TEXT_KEYS.CLUB_REWARD_NOT_ACTIVATED,
            [PENDING]: TEXT_KEYS.CLUB_REWARD_NOT_ACTIVATED
        }
    };

    const actionTypesOrString: string | object = KEYS_PER_STATUS_AND_ACTION_TYPE[redeemActionType];
    if (actionTypesOrString) {
        return typeof actionTypesOrString === 'string' ? actionTypesOrString : actionTypesOrString[status];
    }

    return '';
};
